import { Component } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { type AppLanguageCode, type AppLanguageOption } from '../../core/models/app-language.model';
import {
  type AgriculturalLandRecord,
  type ApartmentUnitRecord,
  type FamilyHouseRecord,
  type LandTransactionRecord,
  type OfferListingRecord
} from '../../core/models/advanced.model';
import { AdvancedService } from '../../core/services/advanced.service';
import { DownloadFeedbackService } from '../../core/services/download-feedback.service';
import { TranslationService } from '../../core/services/translation.service';
import { UiPreferencesService } from '../../core/services/ui-preferences.service';
import { extractHttpErrorMessage } from '../../core/utils/http-error.util';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';
import { FooterComponent } from '../layout/footer/footer.component';
import { NavbarComponent } from '../layout/navbar/navbar.component';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker.component';
import { FileDropzoneComponent } from '../../shared/components/file-dropzone/file-dropzone.component';

const MAX_IMAGES_PER_RECORD = 3;
const DEFAULT_EXPORT_FILE_NAME = 'comparative-valuation.docx';

function createLandTransactionGroup(formBuilder: FormBuilder) {
  return formBuilder.group({
    cadastralArea: ['', Validators.required],
    parcelNumbers: formBuilder.array([
      formBuilder.nonNullable.control('', Validators.required)
    ]),
    totalArea: ['', Validators.required],
    realizedPrice: ['', Validators.required],
    pricePerM2: [''],
    date: ['', Validators.required],
    caseNumber: ['', Validators.required],
    source: ['', Validators.required],
    landUseCategory: ['', Validators.required],
    builtStatus: ['', Validators.required],
    images: formBuilder.array<FormControl<File>>([])
  });
}

function createAgriculturalLandGroup(formBuilder: FormBuilder) {
  return formBuilder.group({
    cadastralArea: ['', Validators.required],
    parcelNumber: ['', Validators.required],
    landType: ['', Validators.required],
    totalArea: ['', Validators.required],
    realizedPrice: ['', Validators.required],
    pricePerM2: [''],
    date: ['', Validators.required],
    caseNumber: [''],
    source: ['', Validators.required],
    images: formBuilder.array<FormControl<File>>([])
  });
}

function createApartmentUnitGroup(formBuilder: FormBuilder) {
  return formBuilder.group({
    cadastralArea: ['', Validators.required],
    unitNumber: ['', Validators.required],
    floorArea: ['', Validators.required],
    realizedPrice: ['', Validators.required],
    pricePerM2: [''],
    date: ['', Validators.required],
    caseNumber: ['', Validators.required],
    source: ['', Validators.required],
    images: formBuilder.array<FormControl<File>>([])
  });
}

function createFamilyHouseGroup(formBuilder: FormBuilder) {
  return formBuilder.group({
    cadastralArea: ['', Validators.required],
    parcelNumber: ['', Validators.required],
    landArea: ['', Validators.required],
    builtArea: ['', Validators.required],
    usableArea: ['', Validators.required],
    realizedPrice: ['', Validators.required],
    pricePerM2: [''],
    date: ['', Validators.required],
    caseNumber: [''],
    source: ['', Validators.required],
    images: formBuilder.array<FormControl<File>>([])
  });
}

function createOfferListingGroup(formBuilder: FormBuilder) {
  return formBuilder.group({
    propertyType: ['', Validators.required],
    location: ['', Validators.required],
    landArea: ['', Validators.required],
    usableArea: ['', Validators.required],
    askingPrice: ['', Validators.required],
    pricePerM2: [''],
    date: ['', Validators.required],
    website: ['', Validators.required],
    images: formBuilder.array<FormControl<File>>([])
  });
}

type LandTransactionFormGroup = ReturnType<typeof createLandTransactionGroup>;
type AgriculturalLandFormGroup = ReturnType<typeof createAgriculturalLandGroup>;
type ApartmentUnitFormGroup = ReturnType<typeof createApartmentUnitGroup>;
type FamilyHouseFormGroup = ReturnType<typeof createFamilyHouseGroup>;
type OfferListingFormGroup = ReturnType<typeof createOfferListingGroup>;

type ValuationTab = 'landTransaction' | 'agriculturalLand' | 'apartmentUnit' | 'familyHouse' | 'offerListing';

@Component({
  selector: 'app-advanced',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, NavbarComponent, FooterComponent, TranslatePipe, DatePickerComponent, FileDropzoneComponent],
  templateUrl: './advanced.component.html',
  styleUrl: './advanced.component.css'
})
export class AdvancedComponent {
  protected readonly languages: readonly AppLanguageOption[];
  protected readonly darkMode;

  protected readonly tabs: readonly ValuationTab[] = [
    'landTransaction',
    'agriculturalLand',
    'apartmentUnit',
    'familyHouse',
    'offerListing'
  ];
  protected activeTab: ValuationTab = 'landTransaction';

  protected readonly landTransactions: FormArray<LandTransactionFormGroup>;
  protected readonly agriculturalLands: FormArray<AgriculturalLandFormGroup>;
  protected readonly apartmentUnits: FormArray<ApartmentUnitFormGroup>;
  protected readonly familyHouses: FormArray<FamilyHouseFormGroup>;
  protected readonly offerListings: FormArray<OfferListingFormGroup>;

  protected isSubmitting = false;
  protected errorMessage = '';
  protected successMessage = '';

  constructor(
    protected readonly translationService: TranslationService,
    private readonly uiPreferences: UiPreferencesService,
    private readonly formBuilder: FormBuilder,
    private readonly advancedService: AdvancedService,
    private readonly downloadFeedback: DownloadFeedbackService
  ) {
    this.languages = this.translationService.languageOptions;
    this.darkMode = this.uiPreferences.darkMode;

    this.landTransactions = this.formBuilder.array([createLandTransactionGroup(this.formBuilder)]);
    this.agriculturalLands = this.formBuilder.array([createAgriculturalLandGroup(this.formBuilder)]);
    this.apartmentUnits = this.formBuilder.array([createApartmentUnitGroup(this.formBuilder)]);
    this.familyHouses = this.formBuilder.array([createFamilyHouseGroup(this.formBuilder)]);
    this.offerListings = this.formBuilder.array([createOfferListingGroup(this.formBuilder)]);
  }

  protected selectTab(tab: ValuationTab): void {
    if (this.activeTab === tab || this.isSubmitting) {
      return;
    }

    this.activeTab = tab;
    this.errorMessage = '';
    this.successMessage = '';
  }

  protected toggleTheme(): void {
    this.uiPreferences.toggleDarkMode();
  }

  protected async setLanguage(language: AppLanguageCode): Promise<void> {
    await this.translationService.setLanguage(language);
  }

  protected parcelNumbersOf(transactionIndex: number): FormArray<FormControl<string>> {
    return this.landTransactions.at(transactionIndex).controls.parcelNumbers;
  }

  protected addParcelNumber(transactionIndex: number): void {
    this.parcelNumbersOf(transactionIndex).push(
      this.formBuilder.nonNullable.control('', Validators.required)
    );
  }

  protected removeParcelNumber(transactionIndex: number, parcelIndex: number): void {
    const parcelNumbers = this.parcelNumbersOf(transactionIndex);

    if (parcelNumbers.length === 1) {
      parcelNumbers.at(0).markAsTouched();
      return;
    }

    parcelNumbers.removeAt(parcelIndex);
  }

  protected parcelNumberHasError(transactionIndex: number, parcelIndex: number): boolean {
    return this.hasError(this.parcelNumbersOf(transactionIndex).at(parcelIndex));
  }

  protected addLandTransaction(): void {
    this.landTransactions.push(createLandTransactionGroup(this.formBuilder));
  }

  protected addAgriculturalLand(): void {
    this.agriculturalLands.push(createAgriculturalLandGroup(this.formBuilder));
  }

  protected addApartmentUnit(): void {
    this.apartmentUnits.push(createApartmentUnitGroup(this.formBuilder));
  }

  protected addFamilyHouse(): void {
    this.familyHouses.push(createFamilyHouseGroup(this.formBuilder));
  }

  protected addOfferListing(): void {
    this.offerListings.push(createOfferListingGroup(this.formBuilder));
  }

  protected resetLandTransactions(): void {
    this.resetRecords(this.landTransactions, () => createLandTransactionGroup(this.formBuilder));
  }

  protected resetAgriculturalLand(): void {
    this.resetRecords(this.agriculturalLands, () => createAgriculturalLandGroup(this.formBuilder));
  }

  protected resetApartmentUnits(): void {
    this.resetRecords(this.apartmentUnits, () => createApartmentUnitGroup(this.formBuilder));
  }

  protected resetFamilyHouses(): void {
    this.resetRecords(this.familyHouses, () => createFamilyHouseGroup(this.formBuilder));
  }

  protected resetOfferListings(): void {
    this.resetRecords(this.offerListings, () => createOfferListingGroup(this.formBuilder));
  }

  private resetRecords<T extends FormGroup>(records: FormArray<T>, factory: () => T): void {
    records.clear();
    records.push(factory());
    this.errorMessage = '';
    this.successMessage = '';
  }

  protected removeRecord(records: FormArray<FormGroup>, index: number): void {
    if (records.length === 1) {
      records.at(0).markAllAsTouched();
      return;
    }

    records.removeAt(index);
  }

  protected imagesOf(records: FormArray<FormGroup>, index: number): FormArray<FormControl<File>> {
    return records.at(index).get('images') as FormArray<FormControl<File>>;
  }

  protected onImagesSelected(files: File[], records: FormArray<FormGroup>, index: number): void {
    const images = this.imagesOf(records, index);
    const availableSlots = Math.max(0, MAX_IMAGES_PER_RECORD - images.length);

    for (const file of files.slice(0, availableSlots)) {
      images.push(this.formBuilder.nonNullable.control(file));
    }
  }

  protected removeImage(records: FormArray<FormGroup>, index: number, imageIndex: number): void {
    this.imagesOf(records, index).removeAt(imageIndex);
  }

  protected imageFileName(records: FormArray<FormGroup>, index: number, imageIndex: number): string {
    const file = this.imagesOf(records, index).at(imageIndex).value;
    return file instanceof File ? file.name : '';
  }

  protected imageFileSize(records: FormArray<FormGroup>, index: number, imageIndex: number): string {
    const file = this.imagesOf(records, index).at(imageIndex).value;
    if (!(file instanceof File)) {
      return '';
    }

    if (file.size < 1024) {
      return `${file.size} B`;
    }

    if (file.size < 1024 * 1024) {
      return `${(file.size / 1024).toFixed(1)} KB`;
    }

    return `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
  }

  protected hasError(control: AbstractControl | null | undefined): boolean {
    if (!control) {
      return false;
    }

    return control.invalid && (control.dirty || control.touched);
  }

  protected submitLandTransactions(): void {
    this.runExport(this.landTransactions, () => this.buildLandTransactionRecords(), (records) =>
      this.advancedService.exportLandTransactions(records)
    );
  }

  protected submitAgriculturalLand(): void {
    this.runExport(this.agriculturalLands, () => this.buildAgriculturalLandRecords(), (records) =>
      this.advancedService.exportAgriculturalLand(records)
    );
  }

  protected submitApartmentUnits(): void {
    this.runExport(this.apartmentUnits, () => this.buildApartmentUnitRecords(), (records) =>
      this.advancedService.exportApartmentUnits(records)
    );
  }

  protected submitFamilyHouses(): void {
    this.runExport(this.familyHouses, () => this.buildFamilyHouseRecords(), (records) =>
      this.advancedService.exportFamilyHouses(records)
    );
  }

  protected submitOfferListings(): void {
    this.runExport(this.offerListings, () => this.buildOfferListingRecords(), (records) =>
      this.advancedService.exportOfferListings(records)
    );
  }

  private runExport<T>(
    records: FormArray<FormGroup>,
    buildPayload: () => T[] | null,
    exportFn: (payload: T[]) => Observable<HttpResponse<Blob>>
  ): void {
    if (this.isSubmitting) {
      return;
    }

    if (records.invalid) {
      records.markAllAsTouched();
      return;
    }

    const payload = buildPayload();
    if (!payload) {
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    exportFn(payload).subscribe({
      next: (response) => {
        const downloaded = this.downloadBlob(response);
        if (downloaded) {
          this.successMessage = this.translationService.translate('advanced.form.success');
        }
        this.isSubmitting = false;
      },
      error: (error: unknown) => {
        this.errorMessage = extractHttpErrorMessage(error, this.translationService.translate('advanced.form.exportError'));
        this.isSubmitting = false;
      }
    });
  }

  private buildLandTransactionRecords(): LandTransactionRecord[] | null {
    const rawRecords = this.landTransactions.getRawValue();
    const records: LandTransactionRecord[] = [];

    for (let index = 0; index < rawRecords.length; index++) {
      const raw = rawRecords[index];
      const parcelNumbers = raw.parcelNumbers.map((value) => value.trim()).filter((value) => value.length > 0);

      if (!parcelNumbers.length) {
        this.parcelNumbersOf(index).markAllAsTouched();
        return null;
      }

      records.push({
        cadastralArea: raw.cadastralArea?.trim() ?? '',
        parcelNumbers,
        totalArea: this.toOptionalNumber(raw.totalArea),
        realizedPrice: this.toOptionalNumber(raw.realizedPrice),
        pricePerM2: this.toOptionalNumber(raw.pricePerM2),
        date: raw.date ?? '',
        caseNumber: raw.caseNumber?.trim() ?? '',
        source: raw.source?.trim() ?? '',
        landUseCategory: raw.landUseCategory?.trim() ?? '',
        builtStatus: raw.builtStatus?.trim() ?? '',
        images: raw.images.filter((file): file is File => file instanceof File)
      });
    }

    return records;
  }

  private buildAgriculturalLandRecords(): AgriculturalLandRecord[] {
    return this.agriculturalLands.getRawValue().map((raw) => ({
      cadastralArea: raw.cadastralArea?.trim() ?? '',
      parcelNumber: raw.parcelNumber?.trim() ?? '',
      landType: raw.landType?.trim() ?? '',
      totalArea: this.toOptionalNumber(raw.totalArea),
      realizedPrice: this.toOptionalNumber(raw.realizedPrice),
      pricePerM2: this.toOptionalNumber(raw.pricePerM2),
      date: raw.date ?? '',
      caseNumber: raw.caseNumber?.trim() ?? '',
      source: raw.source?.trim() ?? '',
      images: raw.images.filter((file): file is File => file instanceof File)
    }));
  }

  private buildApartmentUnitRecords(): ApartmentUnitRecord[] {
    return this.apartmentUnits.getRawValue().map((raw) => ({
      cadastralArea: raw.cadastralArea?.trim() ?? '',
      unitNumber: raw.unitNumber?.trim() ?? '',
      floorArea: this.toOptionalNumber(raw.floorArea),
      realizedPrice: this.toOptionalNumber(raw.realizedPrice),
      pricePerM2: this.toOptionalNumber(raw.pricePerM2),
      date: raw.date ?? '',
      caseNumber: raw.caseNumber?.trim() ?? '',
      source: raw.source?.trim() ?? '',
      images: raw.images.filter((file): file is File => file instanceof File)
    }));
  }

  private buildFamilyHouseRecords(): FamilyHouseRecord[] {
    return this.familyHouses.getRawValue().map((raw) => ({
      cadastralArea: raw.cadastralArea?.trim() ?? '',
      parcelNumber: raw.parcelNumber?.trim() ?? '',
      landArea: this.toOptionalNumber(raw.landArea),
      builtArea: this.toOptionalNumber(raw.builtArea),
      usableArea: this.toOptionalNumber(raw.usableArea),
      realizedPrice: this.toOptionalNumber(raw.realizedPrice),
      pricePerM2: this.toOptionalNumber(raw.pricePerM2),
      date: raw.date ?? '',
      caseNumber: raw.caseNumber?.trim() ?? '',
      source: raw.source?.trim() ?? '',
      images: raw.images.filter((file): file is File => file instanceof File)
    }));
  }

  private buildOfferListingRecords(): OfferListingRecord[] {
    return this.offerListings.getRawValue().map((raw) => ({
      propertyType: raw.propertyType?.trim() ?? '',
      location: raw.location?.trim() ?? '',
      landArea: this.toOptionalNumber(raw.landArea),
      usableArea: this.toOptionalNumber(raw.usableArea),
      askingPrice: this.toOptionalNumber(raw.askingPrice),
      pricePerM2: this.toOptionalNumber(raw.pricePerM2),
      date: raw.date ?? '',
      website: raw.website?.trim() ?? '',
      images: raw.images.filter((file): file is File => file instanceof File)
    }));
  }

  private toOptionalNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const trimmed = typeof value === 'number' ? String(value) : value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private downloadBlob(response: HttpResponse<Blob>): boolean {
    const blob = response.body;

    if (!blob) {
      this.errorMessage = 'Export failed because the file response was empty.';
      return false;
    }

    const fileName = this.extractFileName(response.headers.get('content-disposition'));
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
    this.downloadFeedback.notifyDownloaded(fileName);
    return true;
  }

  private extractFileName(contentDisposition: string | null): string {
    const fileNameUtfMatch = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
    if (fileNameUtfMatch?.[1]) {
      return decodeURIComponent(fileNameUtfMatch[1]);
    }

    const fileNameMatch = contentDisposition?.match(/filename=\"?([^\";]+)\"?/i);
    if (fileNameMatch?.[1]) {
      return fileNameMatch[1];
    }

    return DEFAULT_EXPORT_FILE_NAME;
  }
}
